export type XpSource = "task" | "lesson" | "lesson_repeat" | "topic" | "course";

export type XpBreakdown = { source: XpSource; amount: number };

/** Activity points per event. A lesson with ~3 tasks is worth ~80 points. */
export const XP_REWARDS: Record<XpSource, number> = {
    task: 10,
    lesson: 50,
    lesson_repeat: 10,
    topic: 50,
    course: 150
};

/**
 * Cumulative points needed to *be at* level 1..10. Level 2 = one completed lesson; level 10 lands around
 * lesson 37 of a 41-lesson professional course (7 topic bonuses included); 14-21 lesson courses reach level 7.
 */
export const LEVEL_THRESHOLDS = [0, 50, 150, 300, 525, 825, 1250, 1900, 2550, 3300];
const TABLE_LEVELS = LEVEL_THRESHOLDS.length;
/**
 * Past the table every level costs a little more than the one before (1,000, 1,100, 1,200, ...), so the
 * curve keeps paying out across a whole roadmap: ~7 courses land around level 24, two roadmaps around 33.
 */
const STEP_BEYOND_TABLE = 1000;
const STEP_GROWTH_BEYOND_TABLE = 100;

export type LevelTier = { title: string; fromLevel: number };

/** Titles as i18n keys (English text) with the level that unlocks each; the last one is held for good. */
export const LEVEL_TIERS: LevelTier[] = [
    { title: "Newcomer", fromLevel: 1 },
    { title: "Explorer", fromLevel: 3 },
    { title: "Apprentice", fromLevel: 6 },
    { title: "Practitioner", fromLevel: 10 },
    { title: "Specialist", fromLevel: 15 },
    { title: "Strategist", fromLevel: 20 },
    { title: "Expert", fromLevel: 25 },
    { title: "Architect", fromLevel: 30 },
    { title: "Master", fromLevel: 40 },
    { title: "Luminary", fromLevel: 50 }
];

export function xpForLevel(level: number): number {
    if (level <= 1) return 0;
    if (level <= TABLE_LEVELS) return LEVEL_THRESHOLDS[level - 1];
    const beyond = level - TABLE_LEVELS;
    return (
        LEVEL_THRESHOLDS[TABLE_LEVELS - 1] +
        STEP_BEYOND_TABLE * beyond +
        (STEP_GROWTH_BEYOND_TABLE * beyond * (beyond - 1)) / 2
    );
}

export function levelForXp(xp: number): number {
    const safe = Number.isFinite(xp) && xp > 0 ? xp : 0;
    let level = 1;
    while (safe >= xpForLevel(level + 1)) level++;
    return level;
}

export function tierForLevel(level: number): LevelTier {
    const safe = Math.max(1, level);
    let tier = LEVEL_TIERS[0];
    for (const candidate of LEVEL_TIERS) {
        if (candidate.fromLevel <= safe) tier = candidate;
    }
    return tier;
}

export function levelTitleKey(level: number): string {
    return tierForLevel(level).title;
}

/** The tier after the one the level sits in, or null once the last title is held. */
export function nextTierAfter(level: number): LevelTier | null {
    return LEVEL_TIERS.find((tier) => tier.fromLevel > Math.max(1, level)) ?? null;
}

export type LevelProgress = {
    level: number;
    title: string;
    levelStartXp: number;
    nextLevelXp: number;
    xpIntoLevel: number;
    xpToNext: number;
    ratio: number;
};

export function levelProgress(xp: number): LevelProgress {
    const safe = Number.isFinite(xp) && xp > 0 ? xp : 0;
    const level = levelForXp(safe);
    const levelStartXp = xpForLevel(level);
    const nextLevelXp = xpForLevel(level + 1);
    const span = nextLevelXp - levelStartXp;
    const xpIntoLevel = safe - levelStartXp;
    return {
        level,
        title: levelTitleKey(level),
        levelStartXp,
        nextLevelXp,
        xpIntoLevel,
        xpToNext: Math.max(0, nextLevelXp - safe),
        ratio: span > 0 ? Math.min(1, xpIntoLevel / span) : 1
    };
}

export type CompletionAward = {
    source: "lesson" | "lesson_repeat";
    amount: number;
    breakdown: XpBreakdown[];
};

/** One summed award per completed lesson; the breakdown keeps topic/course bonuses visible to analytics. */
export function xpForCompletion(result: {
    isFirstCompletion: boolean;
    isTopicCompleted: boolean;
    isCourseCompleted: boolean;
}): CompletionAward {
    if (!result.isFirstCompletion) {
        return {
            source: "lesson_repeat",
            amount: XP_REWARDS.lesson_repeat,
            breakdown: [{ source: "lesson_repeat", amount: XP_REWARDS.lesson_repeat }]
        };
    }
    const breakdown: XpBreakdown[] = [{ source: "lesson", amount: XP_REWARDS.lesson }];
    if (result.isTopicCompleted) breakdown.push({ source: "topic", amount: XP_REWARDS.topic });
    if (result.isCourseCompleted) breakdown.push({ source: "course", amount: XP_REWARDS.course });
    return {
        source: "lesson",
        amount: breakdown.reduce((sum, item) => sum + item.amount, 0),
        breakdown
    };
}
